import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { enotaryActions } from '@figure/frontlib-onboarding-redux';
import { Button, Color, Width, Legal, VisuallyHidden } from '@figure/frontlib-gui';
import {
  useLoader,
  usePersonalLoan,
  usePersonalLoanRouteFsm,
  useProfile,
  useRedirect,
  useRedux,
  useTelemetry,
  useTimeout,
} from 'shared/hooks';
import VideoRecorder from 'shared/assets/images/video-recorder.svg';
import { PageHeader } from 'shared/components/Typography';

const StyledButtonWrapper = styled.div`
  text-align: center;

  button {
    margin-bottom: 15px;
  }

  @media print, screen and (min-width: ${Width.SM}px) {
    button {
      margin-bottom: 0;
    }

    button + a {
      margin-left: 15px;
    }
  }
`;

const Card = styled.div`
  border: 2px solid ${Color.BUBBLE_GREY};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  padding: 24px;
  margin-bottom: 16px;
`;

const CardLabel = styled.div`
  font-size: 1.3rem;
  margin-bottom: 16px;
`;

const VideoRecorderImage = styled.img`
  margin-right: 10px;
`;

const CardContent = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.8;
`;

const FundingIntroContainer = () => {
  const dispatch = useDispatch();
  const { uuid: personalLoanUuid } = useParams();
  const history = useHistory();
  const { profile } = useProfile();
  const { personalLoan, loading: personalLoanLoading } = usePersonalLoan();
  const { reservationsByAppId, loading: enotarySchedulingLoading } = useRedux(
    'enotarySchedulingReducer',
    enotaryActions
  );
  const { notaryHours } = useRedux('enotaryReducer', enotaryActions);
  const { telemetry } = useTelemetry();
  const runRouter = usePersonalLoanRouteFsm();
  const { t } = useTranslation();
  const { redirectToDashboard } = useRedirect();

  let content;
  const companySingleName = t('companySingleName');
  const notaryReady = personalLoan.status?.primaryParticipantAcceptsReady;
  const notaryAvailable = notaryHours?.[0]?.businessOpen;
  const canTalkToNotary = notaryReady && notaryAvailable;
  const isHoliday = notaryHours?.[0]?.holiday;
  const [reservation] = reservationsByAppId[personalLoanUuid]?.reservations ?? [];
  const { krangStatus = '' } = personalLoan?.status ?? {};
  const firstName = profile.name?.firstName ?? '';
  const nextUrl = `/applications/${personalLoanUuid}/funding/session`;

  const isLoading = enotarySchedulingLoading || personalLoanLoading;

  useLoader('Loading...', !personalLoan.status?.primaryParticipantAcceptsReady);
  useTimeout(() => {
    if (!personalLoan.status?.primaryParticipantAcceptsReady) {
      history.push(`/applications/${personalLoanUuid}/review-app/timeout`);
    }
  }, 20000);

  useEffect(() => {
    /**
     * If app has gating task move to next spot in flow (App Review)
     * If app has no gating task and is bypass eligible move to next spot in flow (Complete)
     */
    if (
      krangStatus === 'APP_REVIEW' ||
      krangStatus === 'FUNDABLE' ||
      krangStatus === 'CSA_CHECKLIST_COMPLETE'
    )
      runRouter();
  }, [krangStatus, runRouter]);

  useEffect(() => {
    dispatch(enotaryActions.getReservations(personalLoanUuid));
    // Defaulting to CA as endpoint requires a state for manual notary
    // which isnt a thing in PL
    dispatch(enotaryActions.orgNotaryHoursV2({ state: 'CA' }));
  }, [dispatch, personalLoanUuid]);

  if (isLoading) return null;

  const handleCsaClick = () => telemetry('plCSAClick');

  if (canTalkToNotary) {
    content = (
      <Card>
        <CardLabel>
          <VideoRecorderImage src={VideoRecorder} alt="customer success video chat" />
          Video call hours
        </CardLabel>
        <CardContent>6AM-6PM Pacific Time</CardContent>
        <CardContent>Monday - Sunday</CardContent>
      </Card>
    );
  } else if (isHoliday) {
    content =
      'We are currently closed for the holiday. Our Customer Success team will be available on the next business day to help you. ';
  } else {
    content =
      'Our Customer Success team is available Monday - Sunday 6am - 6pm Pacific Time, except for major U.S. holidays.';
  }

  return (
    <>
      <PageHeader>
        Let’s get your funds on their way, <span className="fs-mask">{firstName}</span>
      </PageHeader>
      <h3>
        Connect with a {companySingleName} Customer Success Associate who will confirm some details
        with you and answer any questions you may have.
      </h3>
      <h3>
        Please have the government-issued photo ID (driver's license, US passport, or military ID)
        you used to verify your identity available during the call.
      </h3>
      <p>{content}</p>
      <p>
        {!canTalkToNotary &&
          !reservation &&
          'Please come back to this screen during video call hours to talk to a Customer Success Associate and complete the loan process.'}
        {!canTalkToNotary &&
          reservation &&
          `Your appointment is set for ${format(
            reservation.time,
            'ddd, MMM D, YYYY [at] h:mm a'
          )}.`}
      </p>
      {canTalkToNotary && (
        <>
          <Legal>
            By selecting “Talk to a Customer Success Associate”, you agree to participate in a video
            conference with a {companySingleName} Customer Success Associate who can see and hear
            you in real time. You agree that the video conference will be recorded and the recording
            may be used to administer your loan.{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              See Terms of Service<VisuallyHidden> Opens in a new tab</VisuallyHidden>
            </a>
            .
          </Legal>

          <Legal>
            {companySingleName}’s Customer Success Associates are not licensed to and do not provide
            any legal advice regarding your loan. We encourage you to seek legal advice for any
            unanswered questions about your loan documents.
          </Legal>
        </>
      )}

      <StyledButtonWrapper>
        {canTalkToNotary ? (
          <Link href={nextUrl} to={nextUrl}>
            <Button onClick={handleCsaClick} large primary>
              Talk to a Customer Success Associate
            </Button>
          </Link>
        ) : (
          <Button primary large onClick={redirectToDashboard}>
            Go to dashboard
          </Button>
        )}
      </StyledButtonWrapper>
    </>
  );
};

export default FundingIntroContainer;
